import { useEffect, useState } from "react";
import { useParams } from "react-router";
import { useAuth } from "@/context/AuthContext";
import ResponsiveTable, { Column } from "@/components/Usados/ResponsiveTable";
import ActionMenu from "@/components/Usados/ActionMenu";
import Button from "@/components/ui/button/Button";
import { Modal } from "@/components/Usados/modal";
import { Pencil, Trash } from "lucide-react";
import { Rol } from "@/types/roles";
import { apiFetch } from "@/service/api";

interface Equipo {
  id: number;
  nombre: string;
  descripcion: string;
  actividad?: { id: number; nombre: string };
}
interface ListarEquiposProps {
  onEdit: (equipo: Equipo) => void;
}

export default function ListarEquipos({ onEdit }: ListarEquiposProps) {
  const { id: proyecto_id } = useParams();
  const { user } = useAuth();
  const rolId: number = user.rol;
  const [equipos, setEquipos] = useState<Equipo[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [equipoToDelete, setEquipoToDelete] = useState<number | null>(null);
  const canDelete = rolId !== Rol.COLABORADOR && rolId !== Rol.ASISTENTE;

  const fetchEquipos = async () => {
    try {
      const response = await apiFetch(
        `equipos-proyectos/find-all-proyecto/${proyecto_id}`,
      );
      if (response.ok) {
        const data = await response.json();
        setEquipos(data);
      }
    } catch (error) {
      console.error("Error fetching equipos:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEquipos();
  }, [proyecto_id]);

  const handleDelete = (id: number) => {
    setEquipoToDelete(id);
    setIsModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!equipoToDelete) return;
    try {
      const response = await apiFetch(
        `equipos-proyectos/delete/${equipoToDelete}`,
        { method: "DELETE" },
      );
      if (response.ok) {
        setEquipos(equipos.filter((e) => e.id !== equipoToDelete));
        setIsModalOpen(false);
        setEquipoToDelete(null);
      }
    } catch (error) {
      console.error("Error deleting equipo:", error);
    }
  };

  const truncateText = (text: string) =>
    text.length > 35 ? text.substring(0, 35) + "..." : text;

  const columns: Column<Equipo>[] = [
    {
      label: "Nombre",
      key: "nombre",
      render: (value: any) => truncateText(String(value || "")),
    },
    {
      label: "Descripcion",
      key: "descripcion",
      render: (value: any) => truncateText(String(value || "")),
    },
    {
      label: "Actividad Asociada",
      key: "actividad",
      render: (value: any) =>
        value ? (
          truncateText(value.nombre)
        ) : (
          <span className="text-gray-400 italic">Sin actividad</span>
        ),
    },
    {
      label: "Acciones",
      key: "actions",
      render: (_, row) => (
        <ActionMenu
          actions={[
            {
              label: "Editar",
              icon: <Pencil className="size-4" />,
              onClick: () => onEdit(row),
            },
            ...(canDelete
              ? [
                  {
                    label: "Eliminar",
                    icon: <Trash className="size-4" />,
                    onClick: () => handleDelete(row.id),
                  },
                ]
              : []),
          ]}
        />
      ),
    },
  ];

  return (
    <div>
      <div className="min-h- rounded-2xl border border-gray-200 bg-white px-5 py-7 dark:border-gray-800 dark:bg-white/[0.03] xl:px-10 xl:py-12">
        <h3 className="mb-6 font-semibold text-gray-800 text-center text-theme-xl dark:text-white/90 sm:text-2xl">
          Equipos del Proyecto
        </h3>
        {loading ? (
          <div className="flex items-center justify-center p-10">
            <div className="w-10 h-10 border-4 border-gray-200 border-t-brand-500 rounded-full animate-spin"></div>
          </div>
        ) : equipos.length === 0 ? (
          <div className="text-center py-10">
            <p className="text-gray-500 dark:text-gray-400">
              No hay equipos registrados para este proyecto.
            </p>
          </div>
        ) : (
          <ResponsiveTable columns={columns} data={equipos} />
        )}
      </div>
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        className="max-w- p-6"
      >
        <div className="flex flex-col items-center text-center">
          <div className="flex items-center justify-center w-12 h-12 mb-4 bg-red-100 rounded-full dark:bg-red-500/20">
            <Trash className="text-red-600 size-6 dark:text-red-500" />
          </div>
          <h4 className="mb-2 text-lg font-semibold text-gray-800 dark:text-white/90">
            Confirmar Eliminacion
          </h4>
          <p className="mb-6 text-sm text-gray-500 dark:text-gray-400">
            ¿Estas seguro de que deseas eliminar este equipo?
          </p>
          <div className="flex justify-center w-full gap-3">
            <Button
              variant="outline"
              onClick={() => setIsModalOpen(false)}
              className="w-full"
            >
              Cancelar
            </Button>
            <Button
              onClick={confirmDelete}
              className="w-full bg-red-600 hover:bg-red-700 text-white border-none"
            >
              Eliminar
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
